import React from 'react';
import {
  Grid,
  Box,
  Typography,
  Button,
  useMediaQuery,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReferralData } from '../types';
import { LEAD_STATUS, STATUS, TAB_KEYS } from '../../LeadManagement/constants';
import InfoField from './InfoField';
import { formatDateCell, getNestedProperty } from '../utils';
import { storeCDPProfileData } from '../utils/cdpProfileNavigation';
import FlagPatientButton from './FlagPatientButton';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

/** Parallel arrays for GetSyncedResources: index i is one lead triple. */
export interface CdpProfileSyncTriples {
  guestRefs: string[];
  enrollmentIds: string[];
  leadIds: number[];
}

function isValidCdpProfileSyncTriples(t: CdpProfileSyncTriples): boolean {
  const n = t.guestRefs.length;
  return n > 0 && n === t.enrollmentIds.length && n === t.leadIds.length;
}

interface PatientInfoGridProps {
  row: ReferralData;
  onSendToCC?: () => void;
  isSending?: boolean;
  selectedDedupeRecord?: string;
  isProcessedTab?: boolean;
  fieldConfig?: Array<{ key: string; label: string }>;
  personalInfoHeaderText?: string;
  /** When false, hides the personal-info title row label (actions remain). */
  showPersonalTitle?: boolean;
  /** Overrides default “Lead ID: …” tooltip on the header info icon. */
  infoTooltip?: string;
  statusValue?: string;
  viewDetails?: { href: string; text: string };
  sendToCC?: string;
  // Flag Patient Props
  onFlagPatient?: () => Promise<void>;
  currentUser?: string;
  lockedRecordsCount?: number;
  isLockingEnabled?: boolean;
  currentTabKey?: string;
  /** Processed profile view: batch triples for session + URL; omitted for enrollment-style navigation. */
  cdpProfileSyncTriples?: CdpProfileSyncTriples;
  /** Drives Full CDP URL/session: `profile` adds enrollment-specific UI; `enrollment` keeps classic layout. */
  cdpUiMode?: 'profile' | 'enrollment';
}

const PatientInfoGrid: React.FC<PatientInfoGridProps> = ({
  row,
  onSendToCC,
  isSending = false,
  selectedDedupeRecord,
  isProcessedTab = false,
  fieldConfig,
  personalInfoHeaderText = 'Personal Information',
  showPersonalTitle = true,
  infoTooltip,
  statusValue,
  viewDetails,
  sendToCC,
  onFlagPatient,
  currentUser,
  lockedRecordsCount = 0,
  isLockingEnabled = process.env.NEXT_PUBLIC_LMA_LOCK_ENABLED === 'true',
  currentTabKey,
  cdpProfileSyncTriples,
  cdpUiMode = 'enrollment',
}) => {
  // Custom breakpoint: 4 columns only when screen width > 1200px
  const isLargeScreen = useMediaQuery('(min-width:1200px)');

  const { sitecoreContext } = useSitecoreContext();
  const dateFormat: string =
    (sitecoreContext as any)?.siteSettings?.DateFormat?.value || 'MM/DD/YYYY';

  // Determine if Send To CC button should be disabled based on statusValue and lock state
  // Send To CC is enabled only when the row is locked by the current user, then:
  // NEW - enable (when locked by me)
  // MANUAL_REVIEW_REQUIRED - always disabled
  // RETURNING_USER - disabled when patientCCId is null/empty (no CC record to match against)
  // AUTO_MERGE - enabled only when selectedDedupeRecord has value
  // Also disable when row is locked by someone else, or when current user has locked another row
  const currentStatus = statusValue || row.status;
  const isLockedByCurrentUser = !!(currentUser && row.lockedBy === currentUser);
  const isLockedByOther = !!(row.lockedBy && currentUser && row.lockedBy !== currentUser);
  const hasLockedOtherRow = lockedRecordsCount >= 1 && row.lockedBy !== currentUser;
  const isSendToCCDisabled = isLockingEnabled
    ? !isLockedByCurrentUser ||
      currentStatus === STATUS.MANUAL_REVIEW_REQUIRED ||
      (currentStatus === STATUS.RETURNING_USER && !row.patientCCId) ||
      (currentStatus === STATUS.AUTO_MERGE && !selectedDedupeRecord) ||
      isLockedByOther ||
      hasLockedOtherRow
    : currentStatus === STATUS.MANUAL_REVIEW_REQUIRED ||
      (currentStatus === STATUS.RETURNING_USER && !row.patientCCId) ||
      (currentStatus === STATUS.AUTO_MERGE && !selectedDedupeRecord);

  const cdpProfileId = row.patientId;

  const handleViewCDPProfile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const batchTriples =
      cdpProfileSyncTriples && isValidCdpProfileSyncTriples(cdpProfileSyncTriples)
        ? cdpProfileSyncTriples
        : null;

    const resolvedCdpUiMode = batchTriples && cdpUiMode === 'profile' ? 'profile' : 'enrollment';

    storeCDPProfileData({
      ...row,
      ccPatientId: row.patientId,
      status: currentStatus,
      leadStatus: isProcessedTab ? LEAD_STATUS.PROCESSED : LEAD_STATUS.REQUESTED,
      cdpUiMode: resolvedCdpUiMode,
      ...(batchTriples
        ? {
            guestRef: batchTriples.guestRefs.join(','),
            enrollmentId: batchTriples.enrollmentIds.join(','),
            leadId: batchTriples.leadIds.join(','),
          }
        : {}),
    });

    if (!viewDetails?.href) return;

    const params = new URLSearchParams();
    params.set('id', String(row.patientId));
    if (batchTriples) {
      params.set('leadIds', batchTriples.leadIds.join(','));
      params.set('guestRefs', batchTriples.guestRefs.join(','));
      params.set('enrollmentIds', batchTriples.enrollmentIds.join(','));
      params.set('profileMultiLead', '1');
    }
    if (resolvedCdpUiMode === 'profile') {
      params.set('cdpUiMode', 'profile');
    }

    window.open(`${viewDetails.href}?${params.toString()}`, '_blank');
  };

  // Default field configuration if not provided
  const defaultPersonalFields = [
    { key: 'email', label: 'Email' },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'zipcode', label: 'Zipcode' },
    { key: 'qualifiedProtocols', label: 'Qualified Protocols' },
  ];

  const personalFields =
    fieldConfig && fieldConfig.length > 0 ? fieldConfig : defaultPersonalFields;

  return (
    <Box>
      {/* Personal Information Section with Actions */}
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={1.25}>
          {/* Personal Details Header with Buttons */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 0.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {showPersonalTitle ? (
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      fontSize: '13px',
                      color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                    }}
                  >
                    {personalInfoHeaderText}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Tooltip
                  title={infoTooltip ?? `Lead ID: ${row.leadId ?? row.id}`}
                  placement="top"
                  arrow
                >
                  <IconButton size="small" sx={{ p: 0.25 }} aria-label="Show lead ID">
                    <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {/* Flag Patient Button - LEFT of View Full CDP Profile */}
                {!isProcessedTab && onFlagPatient && (
                  <FlagPatientButton
                    disabled={isSending}
                    onFlagChange={onFlagPatient}
                    loadFlaggedData={row.isFlaggedRecord}
                  />
                )}
                {cdpProfileId &&
                  !(
                    (currentTabKey === TAB_KEYS.NON_REFERRALS ||
                      currentTabKey === TAB_KEYS.PROCESSED_REFERRALS) &&
                    (row.patientReferralStatus?.toLocaleLowerCase() === STATUS.REGISTERED_USER ||
                      (row.leadDetails &&
                        row.leadDetails?.length > 0 &&
                        row.leadDetails?.every(
                          (a) =>
                            a?.patientReferralStatus?.toLocaleLowerCase() === STATUS.REGISTERED_USER
                        )))
                  ) && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleViewCDPProfile}
                      sx={{
                        textTransform: 'none',
                        fontSize: '12px',
                        borderColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                        color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                        '&:hover': {
                          backgroundColor: (theme: any) => theme.palette.themeLightColor?.main,
                          borderColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                        },
                      }}
                    >
                      {viewDetails?.text || 'View Enrollment Details'}
                    </Button>
                  )}
                {onSendToCC && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={onSendToCC}
                    disabled={isSendToCCDisabled || isSending}
                    startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{
                      textTransform: 'none',
                      fontSize: '12px',
                      backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: (theme: any) =>
                          theme.palette.backgroundColorType1?.main || '#1a4545',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#ccc',
                        color: '#888',
                      },
                    }}
                  >
                    {isSending ? 'Sending...' : sendToCC || 'Send to CC'}
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Patient Information Fields */}
          {personalFields.map((field) => {
            let fieldValue = getNestedProperty(row, field.key);
            // Capitalize first letter only for patientType field
            if (field.key === 'patientType' && fieldValue) {
              fieldValue = fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1);
            } else if (field.key === 'age' && fieldValue != null && fieldValue !== '') {
              fieldValue = String(fieldValue);
            } else if (
              (field.key === 'dob' ||
                field.key === 'registrationDateTime' ||
                field.key === 'processedDateTime') &&
              fieldValue
            ) {
              fieldValue = formatDateCell(fieldValue, dateFormat);
            } else if (field.key === 'matchPercentage' && fieldValue) {
              fieldValue = `${Math.min(fieldValue, 100)}%`;
            }
            return (
              <Grid item xs={12} sm={6} md={4} lg={isLargeScreen ? 3 : 4} key={field.key}>
                <InfoField label={field.label} value={fieldValue} />
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};

export default PatientInfoGrid;
